// client/src/components/Comments.js
import React, { useState, useEffect } from 'react';

function isLikelyTrailingPunctuation(ch) {
  return ch === '.' || ch === ',' || ch === ';' || ch === ':' || ch === '!' || ch === '?';
}

function trimUrlMatch(raw) {
  let url = String(raw || '');
  let trailing = '';

  while (url.length && isLikelyTrailingPunctuation(url[url.length - 1])) {
    trailing = `${url[url.length - 1]}${trailing}`;
    url = url.slice(0, -1);
  }

  while (url.length && url.endsWith(')') && (url.match(/\(/g) || []).length < (url.match(/\)/g) || []).length) {
    trailing = `)${trailing}`;
    url = url.slice(0, -1);
  }

  return { url, trailing };
}

function getUrlHref(url) {
  const value = String(url || '').trim();
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
}

function renderCommentMessage(text) {
  const input = String(text || '');
  const parts = [];
  const pattern = /(https?:\/\/[^\s<]+|www\.[^\s<]+|@[A-Za-z0-9_]+)/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(input)) !== null) {
    const raw = match[0];
    const index = match.index;

    if (index > lastIndex) {
      parts.push(input.slice(lastIndex, index));
    }

    if (raw.startsWith('@')) {
      parts.push(
        <span key={`mention-${index}-${raw}`} className="mention">
          {raw}
        </span>
      );
      lastIndex = index + raw.length;
      continue;
    }

    const { url, trailing } = trimUrlMatch(raw);
    const href = getUrlHref(url);

    if (url) {
      parts.push(
        <a key={`url-${index}-${url}`} className="comment-link" href={href} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      );
    }

    if (trailing) {
      parts.push(trailing);
    }

    lastIndex = index + raw.length;
  }

  if (lastIndex < input.length) {
    parts.push(input.slice(lastIndex));
  }

  return parts;
}

function buildThread(comments) {
  const map = {};
  const roots = [];
  comments.forEach(c => {
    c.replies = [];
    map[c.id] = c;
  });
  comments.forEach(c => {
    if (c.parent_comment_id) {
      map[c.parent_comment_id]?.replies.push(c);
    } else {
      roots.push(c);
    }
  });
  return roots;
}

const CommentItem = ({
  comment, user, mapOwnerId, onReplySubmit, replyingTo, setReplyingTo,
  onDelete, onEdit, editingId, setEditingId, onEditSubmit, level = 0
}) => {
  const [editText, setEditText] = useState(comment.message);

  useEffect(() => {
    if (editingId === comment.id) setEditText(comment.message);
  }, [editingId, comment.message, comment.id]);

  const hasReplies = comment.replies && comment.replies.length > 0;

  const canDelete =
    user &&
    (
      user.id === 1 ||
      user.id === mapOwnerId ||
      (
        user.id === comment.user_id &&
        !hasReplies
      )
    );

  return (
    <div style={{
      marginLeft: level * 22,
      marginTop: 16,
      background: '#23282d',
      padding: 14,
      borderRadius: 10,
      border: '1px solid #334',
      color: '#fff',
      position: 'relative'
    }}>
      <div style={{ fontWeight: 700 }}>{comment.username}</div>

      {editingId === comment.id ? (
        <form
          onSubmit={e => {
            e.preventDefault();
            onEditSubmit(comment.id, editText);
            setEditingId(null);
          }}
        >
          <textarea
            rows={2}
            style={{ width: '100%', margin: '6px 0 2px 0' }}
            value={editText}
            onChange={e => setEditText(e.target.value)}
            maxLength={600}
            required
          />
          <button type="submit" style={{ marginTop: 4 }}>Save</button>
          <button
            type="button"
            onClick={() => setEditingId(null)}
            style={{ marginLeft: 6, background: '#333', color: '#fff' }}
          >
            Cancel
          </button>
        </form>
      ) : (
        <div style={{ margin: '6px 0 2px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {renderCommentMessage(comment.message)}
        </div>
      )}

      <div style={{ fontSize: 12, color: '#8c9', opacity: 0.8 }}>
        {new Date(comment.created_at).toLocaleString()}
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <button style={{ fontSize: 12 }} onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}>
          {replyingTo === comment.id ? 'Cancel' : 'Reply'}
        </button>
        {user && user.id === comment.user_id && editingId !== comment.id && (
          <button style={{ fontSize: 12, color: '#feb' }} onClick={() => setEditingId(comment.id)}>
            Edit
          </button>
        )}
        {canDelete && (
          <button
            style={{ fontSize: 12, color: '#f88' }}
            onClick={() => window.confirm("Delete this comment?") && onDelete(comment.id)}
          >
            Delete
          </button>
        )}
      </div>

      {replyingTo === comment.id &&
        <form
          onSubmit={e => {
            e.preventDefault();
            onReplySubmit(comment.id, e.target.elements.reply.value);
            setReplyingTo(null);
            e.target.reset();
          }}
        >
          <textarea name="reply" rows={2} style={{ width: '100%', marginTop: 8 }} maxLength={600} required />
          <button type="submit" style={{ marginTop: 4 }}>Post Reply</button>
        </form>
      }

      {comment.replies && comment.replies.map(r =>
        <CommentItem
          key={r.id}
          comment={r}
          user={user}
          mapOwnerId={mapOwnerId}
          onReplySubmit={onReplySubmit}
          replyingTo={replyingTo}
          setReplyingTo={setReplyingTo}
          onDelete={onDelete}
          onEdit={onEdit}
          editingId={editingId}
          setEditingId={setEditingId}
          onEditSubmit={onEditSubmit}
          level={level + 1}
        />
      )}
    </div>
  );
};

export default function Comments({ mapId, user, mapOwnerId }) {
  const [activeTab, setActiveTab] = useState('comments');

  const [comments, setComments] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/comments/${mapId}`)
      .then(res => res.json())
      .then(data => {
        setComments(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => { setError("Failed to load comments."); setLoading(false); });
  }, [mapId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!user) return setError("You must be logged in to post a comment.");
    setError('');
    const form = e.target;
    const message = form.message.value;
    fetch(`/api/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ map_id: mapId, message })
    })
      .then(res => res.json())
      .then(comment => {
        if (comment.error) { setError(comment.error); return; }
        setComments(prev => [...prev, comment]);
        form.reset();
      })
      .catch(() => setError("Failed to post comment."));
  };

  const handleReplySubmit = (parentId, replyMessage) => {
    if (!user) return setError("You must be logged in to reply.");
    setError('');
    fetch(`/api/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ map_id: mapId, message: replyMessage, parent_comment_id: parentId })
    })
      .then(res => res.json())
      .then(comment => {
        if (comment.error) { setError(comment.error); return; }
        setComments(prev => [...prev, comment]);
      })
      .catch(() => setError("Failed to post reply."));
  };

  const handleDelete = (commentId) => {
    fetch(`/api/comments/${commentId}`, {
      method: 'DELETE',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(result => {
        if (!result.success) return setError("Failed to delete comment.");
        setComments(prev => prev.filter(c => c.id !== commentId && c.parent_comment_id !== commentId));
      })
      .catch(() => setError("Failed to delete comment."));
  };

  const handleEditSubmit = (commentId, newMessage) => {
    fetch(`/api/comments/${commentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ message: newMessage })
    })
      .then(res => res.json())
      .then(updated => {
        if (!updated.id) return setError("Failed to edit comment.");
        setComments(prev => prev.map(c => c.id === commentId ? { ...c, message: updated.message } : c));
      })
      .catch(() => setError("Failed to edit comment."));
  };

  const threaded = buildThread(comments);

  return (
    <div style={{ maxWidth: 700, margin: '20px auto 40px auto', background: '#181c22', borderRadius: 14, padding: 18, border: '1.5px solid #334', color: '#fff' }}>
      <style>{`
        .mention {
          background: #384;
          color: #fff;
          padding: 0 3px;
          border-radius: 4px;
          font-weight: 600;
        }

        .comment-link {
          color: #8fd3ff;
          text-decoration: underline;
          text-underline-offset: 2px;
          overflow-wrap: anywhere;
        }

        .comment-link:hover {
          color: #b7e4ff;
        }
      `}</style>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button
          onClick={() => setActiveTab('comments')}
          style={{
            flex: 1,
            padding: '10px 0',
            borderRadius: 10,
            border: activeTab === 'comments' ? '2px solid #9fffa7' : '1.5px solid #334',
            background: activeTab === 'comments' ? '#223028' : '#20242a',
            color: activeTab === 'comments' ? '#baf7be' : '#ccd',
            fontWeight: 800,
            fontSize: 15,
            cursor: 'pointer'
          }}
        >
          💬 Comments
        </button>

        <button
          onClick={() => setActiveTab('help')}
          style={{
            flex: 1,
            padding: '10px 0',
            borderRadius: 10,
            border: activeTab === 'help' ? '2px solid #ffdf7f' : '1.5px solid #334',
            background: activeTab === 'help' ? '#2a261b' : '#20242a',
            color: activeTab === 'help' ? '#ffdf7f' : '#ccd',
            fontWeight: 800,
            fontSize: 15,
            cursor: 'pointer'
          }}
        >
          ❓ Map Help
        </button>
      </div>

      {activeTab === 'comments' && (
        <>
          <h2 style={{ fontWeight: 800, fontSize: 22, margin: 0, color: '#baf7be' }}>Comments</h2>

          {loading ? <div>Loading comments…</div> : null}
          {error ? <div style={{ color: 'salmon', marginBottom: 8 }}>{error}</div> : null}

          <form onSubmit={handleSubmit} style={{ marginBottom: 24, marginTop: 14 }}>
            <textarea
              name="message"
              rows={3}
              placeholder={user ? "Add a comment…" : "Login to add a comment"}
              disabled={!user}
              maxLength={600}
              style={{ width: '100%', resize: 'vertical', padding: 8, fontSize: 16 }}
              required
            />
            <button type="submit" disabled={!user} style={{ marginTop: 8, fontSize: 16 }}>Post Comment</button>
          </form>

          {threaded.length === 0 && !loading && <div style={{ color: '#99b' }}>No comments yet. Be the first!</div>}
          {threaded.map(c =>
            <CommentItem
              key={c.id}
              comment={c}
              user={user}
              mapOwnerId={mapOwnerId}
              onReplySubmit={handleReplySubmit}
              replyingTo={replyingTo}
              setReplyingTo={setReplyingTo}
              onDelete={handleDelete}
              onEditSubmit={handleEditSubmit}
              editingId={editingId}
              setEditingId={setEditingId}
            />
          )}
        </>
      )}

      {activeTab === 'help' && (
        <div style={{ lineHeight: 1.45, color: '#eee' }} data-nosnippet>
          <h3 style={{ margin: '6px 0 10px 0', fontSize: 18, color: '#ffdf7f' }}>
            How to Use This Map
          </h3>

          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>Drag anywhere on the map to move around.</li>
            <li>Zoom with the scroll wheel or pinch on mobile.</li>
            <li>Toggle POI labels for a clean view or screenshots.</li>
            <li>Click any POI to open full details.</li>
            <li>Filter POIs using the sidebar.</li>
            <li>Hover types in the sidebar to highlight POIs.</li>
            <li>Switch floors using the bottom-left layer dropdown.</li>
            <li>Use region maps via the sidebar dropdown.</li>
            <li>Click anywhere on the map to grab exact coordinates.</li>
            <li>Suggest POIs in comments with coordinates.</li>
            <li>Use personal pins for private notes.</li>
            <li>Share info and discoveries via comments.</li>
            <li>Study routes using pathing tokens.</li>
            <li>Teleport using zone links.</li>
            <li>Vote or favourite maps you find useful.</li>
          </ul>

          {user && user.id === mapOwnerId && (
            <>
              <h3 style={{ margin: '18px 0 8px 0', fontSize: 17, color: '#9fffa7' }}>
                Map Owner Tips
              </h3>

              <ul style={{ paddingLeft: 20, margin: 0 }}>
                <li>Add POIs by clicking the map.</li>
                <li>Edit categories and types in the sidebar.</li>
                <li>Upload region maps for multi-area navigation.</li>
                <li>Adjust map styling in the Styler panel.</li>
                <li>Add SEO text to improve Google results.</li>
                <li>Approve SEO indexing when ready.</li>
                <li>Use overlays for floors and layers.</li>
                <li>Keep POI names short and searchable.</li>
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
